import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useSnackbar } from 'notistack'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import ConfirmDialog from '../internal-alerts/ConfirmDialog'
import CollectionsTable from './CollectionsTable'
import PolicyDialog from './PolicyDialog'
import RehydrateDialog from './RehydrateDialog'
import RunHistory from './RunHistory'
import { kpiCardSx, kpiRowSx, statusBadgeSx } from '../../common/sharedStyles'
import { archivalApi, errorText, fmtBytes, fmtDateTime, reclaimableBytes, resumeModeLabel, sendsToS3, statusLabel, statusTone, sumOf } from './archivalUtils'

/**
 * Data Archival — tiered retention of the big Mongo log collections (ROLE_PROSHIPADMIN only).
 * MAIN keeps the newest `hotMonths`, `<name>_archive` (WARM, same DB) the next `warmMonths`, and older months go to
 * S3 as gzip JSONL. Backend: /api/tools/archive/collections (summary, runs, run, policy, rehydrate, links, cancel).
 */

// The theme capitalises chip labels; keep these in sentence case.
const chipSx = { height: 22, fontSize: '0.75rem', fontWeight: 600, '& .MuiChip-label': { textTransform: 'none' } }

function FlagChip({ label, on }) {
    return <Chip size='small' variant='outlined' color={on ? 'success' : 'default'} label={`${label} ${on ? 'on' : 'off'}`} sx={chipSx} />
}

function Kpi({ label, value, sub, token, loading }) {
    return (
        <Paper sx={kpiCardSx(token)}>
            <Typography variant='caption' sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>{label}</Typography>
            <Typography variant='h5' sx={{ fontWeight: 800, mt: 0.5, fontVariantNumeric: 'tabular-nums', opacity: loading ? 0.4 : 1, whiteSpace: 'nowrap' }}>{loading ? '—' : value}</Typography>
            <Box sx={{ mt: 0.5, minHeight: 22, display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden' }}>{sub}</Box>
        </Paper>
    )
}

export default function DataArchival() {
    const { enqueueSnackbar } = useSnackbar()
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [loadError, setLoadError] = useState('')
    const [refreshing, setRefreshing] = useState(false)
    const [runsReloadKey, setRunsReloadKey] = useState(0)

    // Dialog state: confirm = { kind: 'run' | 'toggle' | 'rehydrate', item, period? }
    const [confirm, setConfirm] = useState(null)
    const [busy, setBusy] = useState(false)
    const [editItem, setEditItem] = useState(null)
    const [policyError, setPolicyError] = useState('')
    const [rehydrateItem, setRehydrateItem] = useState(null)

    const loadSummary = useCallback((silent = false) => {
        if (!silent) setLoading(true)
        return archivalApi.getSummary()
            .then((res) => { setData(res.data || null); setLoadError('') })
            .catch((err) => { if (!silent) setLoadError(errorText(err, 'Failed to load archive summary')) })
            .finally(() => { if (!silent) setLoading(false) })
    }, [])

    useEffect(() => { loadSummary() }, [loadSummary])

    const afterChange = useCallback(() => {
        loadSummary(true)
        setRunsReloadKey((k) => k + 1)
    }, [loadSummary])
    const onRunsSettled = useCallback(() => { loadSummary(true) }, [loadSummary])

    // Show a saved policy straight away; the summary reload that follows takes a few seconds on a remote cluster.
    const applyPolicy = useCallback((name, policy) => {
        if (!policy) return
        setData((d) => (d ? { ...d, collections: (d.collections || []).map((c) => (c.collectionName === name ? { ...c, policy } : c)) } : d))
    }, [])

    const refreshSizes = () => {
        setRefreshing(true)
        archivalApi.refreshSummary()
            .then((res) => { setData(res.data || null); setLoadError(''); enqueueSnackbar('Sizes refreshed', { variant: 'success' }) })
            .catch((err) => enqueueSnackbar(errorText(err, 'Failed to refresh sizes'), { variant: 'error' }))
            .finally(() => setRefreshing(false))
    }

    const collections = useMemo(() => data?.collections || [], [data])
    const collectionNames = useMemo(() => collections.map((c) => c.collectionName).filter(Boolean), [collections])
    const globalS3Enabled = Boolean(data?.s3Enabled)

    const kpis = useMemo(() => {
        const live = sumOf(collections, (c) => c.summary?.main?.storageBytes)
        const archive = sumOf(collections, (c) => c.summary?.warm?.storageBytes)
        const s3 = sumOf(collections, (c) => c.summary?.s3?.bytes)
        const reclaim = sumOf(collections, (c) => reclaimableBytes(c.summary))
        let last = null
        collections.forEach((c) => {
            const at = c.summary?.lastRunAt
            if (at && (!last || new Date(at) > new Date(last.at))) last = { at, status: c.summary.lastStatus, name: c.collectionName }
        })
        const toS3 = collections.filter((c) => c.policy && sendsToS3(c.policy, globalS3Enabled)).length
        return { live, archive, s3, reclaim, last, toS3 }
    }, [collections, globalS3Enabled])

    const onAction = useCallback((kind, item) => {
        if (kind === 'edit') { setPolicyError(''); setEditItem(item); return }
        if (kind === 'rehydrate') { setRehydrateItem(item); return }
        setConfirm({ kind, item })
    }, [])

    const doConfirm = () => {
        if (!confirm) return
        const { kind, item, period } = confirm
        const name = item.collectionName
        let call
        if (kind === 'run') {
            call = archivalApi.runNow(name).then((res) => {
                const queued = res.data?.queued ?? 0
                const msg = res.data?.message ? ` — ${res.data.message}` : ''
                enqueueSnackbar(`Queued ${queued} run${queued === 1 ? '' : 's'} for ${name}${msg}`, { variant: queued ? 'success' : 'info' })
            })
        } else if (kind === 'toggle') {
            const enabled = !item.policy.enabled
            call = archivalApi.savePolicy(name, { ...item.policy, enabled }).then((res) => {
                applyPolicy(name, res.data)
                enqueueSnackbar(`${name} ${enabled ? 'resumed' : 'paused'}`, { variant: 'success' })
            })
        } else if (kind === 'rehydrate') {
            call = archivalApi.rehydrate(name, period).then(() => {
                enqueueSnackbar(`Rehydrate of ${name} ${period} queued`, { variant: 'success' })
            })
        } else {
            return
        }
        setBusy(true)
        call
            .then(() => { setConfirm(null); afterChange() })
            .catch((err) => { setConfirm(null); enqueueSnackbar(errorText(err, 'Request failed'), { variant: 'error' }) })
            .finally(() => setBusy(false))
    }

    const savePolicy = (policy) => {
        if (!editItem) return
        const name = editItem.collectionName
        setBusy(true)
        setPolicyError('')
        archivalApi.savePolicy(name, policy)
            .then((res) => {
                applyPolicy(name, res.data)
                enqueueSnackbar(`Retention saved for ${name}`, { variant: 'success' })
                setEditItem(null)
                afterChange()
            })
            // Shown once, inline in the dialog — no toast.
            .catch((err) => setPolicyError(errorText(err, 'Failed to save retention')))
            .finally(() => setBusy(false))
    }

    const confirmText = () => {
        if (!confirm) return null
        const { kind, item, period } = confirm
        const name = <b>{item.collectionName}</b>
        if (kind === 'run') {
            return (
                <>
                    Queue an archive run for {name} now? It keeps {item.policy?.hotMonths} month(s) in the collection and
                    {' '}{item.policy?.warmMonths} in the archive, moving at most {item.policy?.maxMonthsPerRun ?? '—'} month(s) in this run.
                    {item.policy?.dryRun && <> The policy is in <b>dry run</b> — nothing will actually move.</>}
                </>
            )
        }
        if (kind === 'toggle') {
            return item.policy?.enabled
                ? <>Pause archiving for {name}? The scheduled job will skip it until resumed, and runs already queued for it are skipped.</>
                : <>Resume archiving for {name} in <b>{resumeModeLabel(item.policy)}</b> mode? The scheduled job will pick it up on its next run.</>
        }
        if (kind === 'rehydrate') {
            return <>Restore {name} for <b>{period}</b> from S3? The month is copied back into {item.warmCollectionName || `${item.collectionName}_archive`}.</>
        }
        return null
    }
    const confirmTitle = !confirm ? '' : confirm.kind === 'run' ? 'Run now' : confirm.kind === 'rehydrate' ? 'Rehydrate month' : confirm.item.policy?.enabled ? 'Pause archiving' : 'Resume archiving'

    return (
        <>
            <PageTitle>Data Archival</PageTitle>
            <Page title=''>
                {/* ── status strip: global engine flags ── */}
                <Stack direction='row' alignItems='center' flexWrap='wrap' sx={{ gap: 1, mb: 2 }}>
                    {data ? (
                        <>
                            <FlagChip label='Engine' on={Boolean(data.engineEnabled)} />
                            <FlagChip label='S3 tier' on={globalS3Enabled} />
                            <Tooltip title='S3 bucket / prefix' arrow>
                                <Typography component='span' sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary', ml: 0.5 }}>
                                    {`s3://${data.bucket || '—'}/${data.s3Prefix || ''}`}
                                </Typography>
                            </Tooltip>
                        </>
                    ) : (
                        <Typography variant='body2' sx={{ color: 'text.secondary' }}>{loading ? 'Loading archive status…' : 'Archive status unavailable'}</Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <LoadingButton loading={refreshing} variant='outlined' size='small' startIcon={<RefreshIcon />} onClick={refreshSizes}
                        sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        Refresh sizes
                    </LoadingButton>
                </Stack>

                {loadError && <Alert severity='error' sx={{ mb: 2 }}>{loadError}</Alert>}

                {/* ── KPIs ── */}
                <Box sx={kpiRowSx}>
                    <Kpi label='In live collections' token='primary' loading={loading && !data} value={fmtBytes(kpis.live)}
                        sub={`${collections.length} collection${collections.length === 1 ? '' : 's'}`} />
                    <Kpi label='In archive' token='info' loading={loading && !data} value={fmtBytes(kpis.archive)}
                        sub='Same database' />
                    <Kpi label='In S3' token='success' loading={loading && !data} value={fmtBytes(kpis.s3)}
                        sub={globalS3Enabled ? `${kpis.toS3} collection${kpis.toS3 === 1 ? '' : 's'} sending to S3` : 'S3 tier is off'} />
                    <Kpi label='Reclaimable' token='warning' loading={loading && !data} value={fmtBytes(kpis.reclaim)}
                        sub={kpis.reclaim > 0 ? 'Returned by a compact' : 'Nothing to reclaim'} />
                    <Kpi label='Last run' token='secondary' loading={loading && !data} value={kpis.last ? fmtDateTime(kpis.last.at) : '—'}
                        sub={kpis.last ? (
                            <>
                                {kpis.last.status && <Box component='span' sx={{ ...statusBadgeSx(statusTone(kpis.last.status)), fontSize: '0.6875rem', py: 0 }}>{statusLabel(kpis.last.status)}</Box>}
                                <Box component='span' sx={{ fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis' }}>{kpis.last.name}</Box>
                            </>
                        ) : 'No runs yet'} />
                </Box>

                {/* ── collections ── */}
                <Paper sx={{ p: 2, mb: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Stack direction='row' alignItems='baseline' sx={{ gap: 1, mb: 1.5 }}>
                        <Typography variant='subtitle1' sx={{ fontWeight: 700 }}>Collections</Typography>
                        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                            Newest data stays in the live collection, then moves to the archive collection, then to S3. Hover a size for dates and index sizes.
                        </Typography>
                    </Stack>
                    <CollectionsTable collections={collections} globalS3Enabled={globalS3Enabled} loading={loading && !data} onAction={onAction} />
                    {data && collections.length === 0 && (
                        <Typography variant='body2' sx={{ color: 'text.secondary', mt: 1 }}>No collections are configured for archival.</Typography>
                    )}
                </Paper>

                {/* ── run history ── */}
                <RunHistory collectionNames={collectionNames} globalS3Enabled={globalS3Enabled} reloadKey={runsReloadKey} onSettled={onRunsSettled} />

                <ConfirmDialog
                    open={Boolean(confirm)}
                    title={confirmTitle}
                    confirmLabel={confirm?.kind === 'run' ? 'Queue runs' : confirm?.kind === 'rehydrate' ? 'Queue rehydrate' : confirm?.item?.policy?.enabled ? 'Pause' : 'Resume'}
                    tone={confirm?.kind === 'toggle' && confirm?.item?.policy?.enabled ? 'warning' : 'primary'}
                    busy={busy}
                    onCancel={() => setConfirm(null)}
                    onConfirm={doConfirm}>
                    {confirmText()}
                </ConfirmDialog>
                <PolicyDialog
                    open={Boolean(editItem)}
                    item={editItem}
                    floors={editItem?.floors || {}}
                    globalS3Enabled={globalS3Enabled}
                    busy={busy}
                    error={policyError}
                    onCancel={() => setEditItem(null)}
                    onSave={savePolicy}
                />
                <RehydrateDialog
                    open={Boolean(rehydrateItem)}
                    item={rehydrateItem}
                    onCancel={() => setRehydrateItem(null)}
                    onSubmit={(period) => { const item = rehydrateItem; setRehydrateItem(null); setConfirm({ kind: 'rehydrate', item, period }) }}
                />
            </Page>
        </>
    )
}
